// src/DevStreamPane.js
import React, { useState, useEffect, useRef } from "react";
import DevStreamControls from "./DevStreamControls";
import DevStreamFilters from "./DevStreamFilters";
import DevStreamView from "./DevStreamView";
import DevStreamOrgSelector from "./DevStreamOrgSelector";
import "./DevStream.css";

const STORAGE_KEY_WIDTH = "devStream_paneWidth";
const DEFAULT_WIDTH = 40; // 40% of viewport
const MIN_WIDTH = 20; // 20% minimum
const MAX_WIDTH = 60; // 60% maximum

/**
 * DevStreamPane - Resizable pane for dev streams
 *
 * Features:
 * - Resizable with drag handle (20-60% of viewport)
 * - Default 40% width
 * - Persists width to localStorage
 * - Contains controls, filters, and event view
 * - Can pop out to separate window
 */
const DevStreamPane = ({
  events,
  filters,
  updateFilter,
  ypEnabled,
  mcEnabled,
  toggleYP,
  toggleMC,
  clearEvents,
  sendTestEvent,
  connected,
  authenticated,
  connectionError,
  onClose,
  switchOrganization, // New prop for organization switching
  selectedOrgId, // Current selected organization ID
  maxEvents, // Current max events limit
  updateMaxEvents, // Function to update max events limit
  isPopout = false, // Flag to indicate if this is a popout window
  isFullAdmin = false, // Flag to show special organization options for Full Admins
  onWidthChange, // Callback to notify parent of width changes
  organizations, // Organizations array to avoid localStorage race condition
}) => {
  const [width, setWidth] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY_WIDTH);
    return stored ? parseInt(stored, 10) : DEFAULT_WIDTH;
  });

  const [isResizing, setIsResizing] = useState(false);
  const paneRef = useRef(null);
  const popoutWindowRef = useRef(null);

  // Handle pop-out to new window
  const handlePopout = () => {
    // Ensure organization context is preserved for popout
    if (selectedOrgId) {
      localStorage.setItem("devStream_selectedOrgId", selectedOrgId);
    } else {
      // Fallback to main app's selected organization
      const mainAppOrgId = localStorage.getItem("selectedOrgId");
      if (mainAppOrgId) {
        localStorage.setItem("devStream_selectedOrgId", mainAppOrgId);
      }
    }

    // Open new window with reasonable default size
    const width = 800;
    const height = 600;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const popoutWindow = window.open(
      `${window.location.origin}/devstream-popout`,
      "DevStreamPopout",
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,location=no,menubar=no,toolbar=no,status=no,titlebar=no,chrome=no`
    );

    if (popoutWindow) {
      popoutWindowRef.current = popoutWindow;

      // Store flag that popout is open
      localStorage.setItem("devStreamPopout", "true");

      // Close the inline pane when popout opens
      if (onClose) {
        onClose();
      }

      // Listen for popout window close
      const checkWindowClosed = setInterval(() => {
        if (popoutWindow.closed) {
          clearInterval(checkWindowClosed);
          localStorage.removeItem("devStreamPopout");
          popoutWindowRef.current = null;
        }
      }, 500);
    }
  };

  // Handle pop-in from popout window
  const handlePopin = () => {
    // Signal to main window to reopen inline pane
    localStorage.setItem("devStreamPopinRequested", "true");

    // Close this popout window
    window.close();
  };

  // Persist width to localStorage and notify parent
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_WIDTH, width.toString());
    // Notify parent component of width change (only in inline mode)
    if (!isPopout && onWidthChange) {
      onWidthChange(width);
    }
  }, [width, isPopout, onWidthChange]);

  // Handle drag to resize
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;

      const viewportWidth = window.innerWidth;
      const newWidth = ((viewportWidth - e.clientX) / viewportWidth) * 100;

      // Clamp to min/max
      const clampedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
      setWidth(Math.round(clampedWidth));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "ew-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing]);

  return (
    <div
      ref={paneRef}
      className={`dev-stream-pane ${isPopout ? "dev-stream-popout" : ""}`}
      style={isPopout ? {} : { width: `${width}vw` }}
    >
      {/* Resize handle - only show in inline mode */}
      {!isPopout && (
        <div
          className="dev-stream-resize-handle"
          onMouseDown={handleMouseDown}
          title="Drag to resize"
        />
      )}

      {/* Header */}
      <div className="dev-stream-header">
        <h2>Developer: Event Streams</h2>
        <div className="dev-stream-header-right">
          {connected ? (
            authenticated ? (
              <span className="dev-stream-status connected">● Connected</span>
            ) : (
              <span className="dev-stream-status authenticating">
                ⋯ Authenticating
              </span>
            )
          ) : (
            <span className="dev-stream-status disconnected">
              ○ Disconnected
            </span>
          )}
          {!isPopout && (
            <button
              className="dev-stream-popout-btn"
              onClick={handlePopout}
              title="Pop out to new window"
            >
              ⧉
            </button>
          )}
          {isPopout && (
            <button
              className="dev-stream-popin-btn"
              onClick={handlePopin}
              title="Pop back into main window"
            >
              ⧈
            </button>
          )}
          {onClose && (
            <button
              className="dev-stream-close-btn"
              onClick={onClose}
              title="Close"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Connection Error */}
      {connectionError && (
        <div className="dev-stream-error">
          <span className="error-icon">⚠️</span>
          {connectionError}
        </div>
      )}

      {/* Organization Selector */}
      <DevStreamOrgSelector
        selectedOrgId={selectedOrgId}
        onOrganizationChange={switchOrganization}
        isFullAdmin={isFullAdmin}
        organizations={organizations}
      />

      {/* Controls */}
      <DevStreamControls
        ypEnabled={ypEnabled}
        mcEnabled={mcEnabled}
        toggleYP={toggleYP}
        toggleMC={toggleMC}
        clearEvents={clearEvents}
        sendTestEvent={sendTestEvent}
        authenticated={authenticated}
        eventCount={events.length}
        maxEvents={maxEvents}
        updateMaxEvents={updateMaxEvents}
      />

      {/* Filters */}
      <DevStreamFilters filters={filters} updateFilter={updateFilter} />

      {/* Event View */}
      <DevStreamView events={events} maxEvents={maxEvents} />
    </div>
  );
};

export default DevStreamPane;
